import { DAY, fromIso, granularity, idxFor, rangeDayCount } from "./dates";
import { MON } from "./constants";
import type { Bucket, DateRange, Delta, Metric, MetricState } from "./types";

/* ---------- Aggregation over the seeded series ---------- */
export function aggregate(metric: Metric, s0: number, s1: number): number {
  const a = metric.daily.slice(s0, s1 + 1);
  if (!a.length) return 0;
  if (metric.agg === "sum") return a.reduce((x, y) => x + y, 0);
  if (metric.agg === "snapshot") return a[a.length - 1];
  return a.reduce((x, y) => x + y, 0) / a.length;
}

export function computeValue(metric: Metric, r: DateRange): number {
  return aggregate(metric, idxFor(fromIso(r.start)), idxFor(fromIso(r.end)));
}

export function computeDelta(metric: Metric, r: DateRange): Delta {
  const n = rangeDayCount(r);
  const e1 = idxFor(fromIso(r.end)), s1 = idxFor(fromIso(r.start));
  const cur = aggregate(metric, s1, e1);
  const prev = aggregate(metric, Math.max(0, s1 - n), Math.max(0, s1 - 1));
  if (prev === 0) return { pct: 0, abs: cur - prev };
  return { pct: ((cur - prev) / prev) * 100, abs: cur - prev };
}

export function stateFor(metric: Metric, value: number): MetricState {
  if (metric.benchmark == null) return "neutral";
  const beats = metric.lowerBetter ? value <= metric.benchmark : value >= metric.benchmark;
  return beats ? "good" : "bad";
}

export function deltaGoodness(metric: Metric, deltaPct: number): MetricState {
  if (Math.abs(deltaPct) < 0.05) return "neutral";
  const up = deltaPct > 0;
  if (metric.lowerBetter) return up ? "bad" : "good";
  return up ? "good" : "bad";
}

/** Trailing 12 buckets ending at range.end, sized by the range granularity. */
export function trendBuckets(metric: Metric, r: DateRange): Bucket[] {
  const g = granularity(r);
  const end = fromIso(r.end);
  const buckets: Bucket[] = [];
  for (let k = 11; k >= 0; k--) {
    let s0: number, s1: number, label: string;
    if (g === "month") {
      const mEnd = new Date(end.getFullYear(), end.getMonth() - k + 1, 0);
      const mStart = new Date(end.getFullYear(), end.getMonth() - k, 1);
      s0 = idxFor(mStart); s1 = idxFor(mEnd); label = `${MON[mStart.getMonth()]} '${String(mStart.getFullYear()).slice(2)}`;
    } else if (g === "week") {
      const wEnd = new Date(end.getTime() - k * 7 * DAY);
      const wStart = new Date(wEnd.getTime() - 6 * DAY);
      s0 = idxFor(wStart); s1 = idxFor(wEnd); label = `${MON[wStart.getMonth()]} ${wStart.getDate()} '${String(wStart.getFullYear()).slice(2)}`;
    } else {
      const d = new Date(end.getTime() - k * DAY);
      s0 = s1 = idxFor(d); label = `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(2)}`;
    }
    buckets.push({ label, value: aggregate(metric, s0, s1) });
  }
  return buckets;
}

/** Deterministic per-branch multiplier so branch numbers differ but are stable. */
export function branchMult(branch: string, id: string): number {
  let h = 0;
  const s = branch + id;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 1000;
  return 0.82 + (h / 1000) * 0.36;
}
